import { styled } from '@mui/material/styles'
import { isEmpty, reduce } from 'ramda'
import React from 'react'

import PzMenu, { PzMenuRef } from '@/app/component/atom/pz-menu'
import { PersonalizationType } from '@/app/types'

import PersonalizationInput from './personalization-input'

type Props = {
  helperText?: string
  id: string
  name: string
  error?: string
  label?: string
  multiline?: boolean
  required?: boolean
  rows?: number
  type?: string
  personalizationList?: PersonalizationType
  value: string
  onChange: (value: string) => void
}

// Edits email text with inspectable personalization labels and insertion at the cursor.
const EmailTextField: React.FC<Props> = (props) => {
  const { personalizationList = {}, value, onChange } = props
  const inputRef = React.useRef<HTMLInputElement>()
  const highlightContainerRef = React.useRef<HTMLDivElement>()
  const [selectionStart, setSelectionStart] = React.useState(0)

  const menuRef = React.useRef<PzMenuRef | null>(null)

  React.useEffect(() => {
    if (!inputRef.current || document.activeElement === inputRef.current || selectionStart === undefined) {
      return
    }

    inputRef.current?.focus()
  }, [selectionStart])

  const updateSelectionStart = () => {
    if (!inputRef.current || typeof inputRef.current.selectionStart !== 'number') {
      return
    }

    setSelectionStart(inputRef.current.selectionStart)
  }

  const resetSelectionPosition = (updatedSelectionPosition: number) => {
    setTimeout(() => {
      if (!inputRef.current) {
        return
      }

      inputRef.current.setSelectionRange(updatedSelectionPosition, updatedSelectionPosition)
    }, 0)
  }

  const onKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    chunks: { index: number; startIndex: number; value: string }[],
  ) => {
    if (!(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
      return
    }
    if (e.key === 'Backspace') {
      if (inputRef.current?.selectionStart !== inputRef.current?.selectionEnd) {
        return
      }

      const gotChunk = checkIfProtected({ pos: e.target.selectionStart || 0, chunks })
      if (!isEmpty(gotChunk)) {
        const tempSelectionStart = inputRef.current?.selectionStart || selectionStart
        e.preventDefault()
        const { startIndex = 0, value: matchedString } = gotChunk
        if (matchedString) {
          onChange(`${value.slice(0, startIndex)}${value.slice(startIndex + matchedString.length)}`)
          resetSelectionPosition(tempSelectionStart - matchedString.length)
        }
      }
    }
    if (e.key === '@') {
      if (!inputRef.current) {
        return
      }
      const currentSelection = inputRef.current.selectionStart || 0
      if (currentSelection < 1 || value.charAt(currentSelection - 1) === ' ') {
        menuRef.current?.open()
        e.preventDefault()
      }
    }
  }

  const Wrapper = props.multiline ? BelowFieldMenuWrapper : FloatingMenuWrapper

  return (
    <div
      className={props.id}
      style={{
        marginBottom: '25px',
        position: 'relative',
      }}
    >
      <PersonalizationInput
        {...props}
        autoFocus
        variant="outlined"
        fullWidth
        size="small"
        onKeyDown={onKeyDown}
        onSelect={updateSelectionStart}
        inputRef={inputRef}
        onChange={(e: { name: string; value: string }) => onChange(e.value)}
        personalizationList={personalizationList}
        highlightContainerRef={highlightContainerRef}
        scrollable={true}
      />
      <Wrapper>
        <PzMenu
          id="email-text-field"
          personalizationList={personalizationList}
          variant={props.multiline ? 'outlined' : undefined}
          ref={menuRef}
          onSelect={(item) => {
            const toBeAdded = `{{${item.label}}}`
            const newMessage = `${value.slice(0, selectionStart)}${toBeAdded}${value.slice(selectionStart)}`
            onChange(newMessage)

            const newSelectionStart = (inputRef.current?.selectionStart || selectionStart) + toBeAdded.length

            resetSelectionPosition(newSelectionStart)
            setSelectionStart(newSelectionStart)
          }}
        />
      </Wrapper>
    </div>
  )
}

const checkIfProtected = ({ pos, chunks }: { pos: number; chunks: { startIndex: number; value: string }[] }) =>
  reduce(
    (accum: { startIndex?: number; value?: string }, chunk) => {
      const { startIndex, value } = chunk
      if (
        value.length > 0 &&
        value.startsWith('{{') &&
        value.endsWith('}}') &&
        startIndex <= pos &&
        startIndex + value.length >= pos
      ) {
        return chunk
      }
      return accum
    },
    {},
    chunks,
  )

const BelowFieldMenuWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: '4px',
}))

const FloatingMenuWrapper = styled('div')(() => ({
  position: 'absolute',
  right: '2px',
  top: '4px',
  zIndex: 999,
  backgroundColor: 'white',
  borderRadius: '50%',
}))

export default EmailTextField
